import moment from "moment";
import _ from "lodash";
import { resolveRpqStatus } from "./clientRpqStatus";
import { resolveMinimumNonWrapUpfrontFee } from "./investmentTransactionSettingsUtils";
import { resolveNonWrapEffectiveMinimum } from "./investmentTransactionCommissionUtils";
import {
  getDerivativesFundPurchaseError,
  resolveDerivativesEligibilityClient,
} from "./derivativesFundEligibility";
import {
  getRpqFundPurchaseError,
  resolveClientRpqFundContext,
} from "./rpqFundEligibility";

export const RPQ_RISK_LABELS = {
  1: "Preservation 保本",
  2: "Conservative 穩健",
  3: "Balanced 均衡",
  4: "Growth 增長",
  5: "Aggressive 進取",
};

/** Mantine v5 NumberInput display formatting for AR Form fields */
const formatArFormNumber = (value) => {
  if (value === "" || value == null) return "";
  if (value === "-") return "-";
  if (Number.isNaN(parseFloat(value))) return String(value);
  const [intPart, decPart] = String(value).split(".");
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
};

const parseArFormNumber = (value) => value?.replace(/[^0-9\-.]/g, "");

/** Shared Mantine NumberInput formatting for AR Form fields */
export const AR_FORM_NUMBER_INPUT_PROPS = {
  formatter: formatArFormNumber,
  parser: parseArFormNumber,
};

const TRANSACTION_TYPES = ["Subscription", "RSP", "Switching"];

export const normalizeTransactionTypes = (value) => {
  const values = Array.isArray(value)
    ? value
    : value == null || value === ""
      ? []
      : [value];
  return [...new Set(values.filter((item) => TRANSACTION_TYPES.includes(item)))];
};

export const formatTransactionTypesForDisplay = (value) => {
  const types = normalizeTransactionTypes(value);
  return types.length ? types.join(", ") : "";
};

export const EMPTY_AR_FORM_PRODUCT = () => ({
  fundName: "",
  riskRating: null,
  trailerFee: null,
  complexProduct: false,
  affiliatedFwd: false,
  fundCurrency: null,
  subscribedAmount: null,
  contractNo: "",
  minSubscriptionAmount: null,
  productSubscribed: false,
  derivativesFund: false,
});

export const validateSubscribedAmount = (value, values = {}) => {
  if (value === "" || value == null) {
    return "Subscribed/ switch-in amount is required";
  }

  const amount = Number(value);
  if (Number.isNaN(amount) || amount <= 0) {
    return "Subscribed/ switch-in amount must be greater than 0";
  }

  const minimum = values.minSubscriptionAmount;
  const currency = values.fundCurrency || "USD";
  if (minimum != null && amount < minimum) {
    return `Minimum subscribed/ switch-in amount is ${minimum.toLocaleString()} ${currency}`;
  }

  return null;
};

export const normalizeArFormProduct = (item) => {
  if (!item) return EMPTY_AR_FORM_PRODUCT();
  if (typeof item === "string") {
    return { ...EMPTY_AR_FORM_PRODUCT(), fundName: item };
  }
  return { ...EMPTY_AR_FORM_PRODUCT(), ...item };
};

export const normalizeAlternativesConsideredList = (items) => {
  if (!Array.isArray(items)) return [];
  return items.map(normalizeArFormProduct);
};

/** Pad to 2 rows for PDF printing */
export const normalizeAlternativesConsideredForPrint = (items) =>
  [0, 1].map((i) => normalizeArFormProduct(items?.[i]));

const coerceArFormNumber = (value) => {
  if (value === "" || value == null) return undefined;
  const num = Number(value);
  return Number.isNaN(num) ? undefined : num;
};

export const DEFAULT_AR_FORM = () => ({
  submissionVia: "Online",
  transactionType: [],
  investmentObjective: [],
  alternativesConsidered: [],
  recommendedProducts: [],
  declaredNetWealth: undefined,
  concentrationRiskLevel: undefined,
  complexProductConcentration: undefined,
  suitability: {
    riskTolerance: false,
    objective: false,
    horizon: false,
    mismatchRemark: "",
  },
  commission: {
    platformFee: 0.3,
  },
  clientDeclaration: {
    keyFactSheet: false,
    prospectus: false,
    fundFactSheet: false,
    other: false,
    otherSpecify: "",
  },
  advisorDeclaration: {
    signature: "",
    signDate: undefined,
    staffCode: "",
    sfcCeNo: "",
    remark: "",
  },
});

/** Order-level AR account fields (stored on order.arForm, not per transaction). */
export const normalizeOrderArFormAccount = (arForm) => ({
  ifastAccountNo: arForm?.ifastAccountNo ?? "",
});

export const resolveOrderIfastAccountNo = ({
  policyNumber,
  ifastAccountNo,
} = {}) => {
  const trimmedIfast = String(ifastAccountNo ?? "").trim();
  if (trimmedIfast) return trimmedIfast;
  return String(policyNumber ?? "").trim();
};

export const setOrderIfastAccountNo = (form, value) => {
  form?.markUserEdited?.();
  form?.setFieldValue("arForm", {
    ...normalizeOrderArFormAccount(form?.values?.arForm),
    ifastAccountNo: value,
  });
};

export const isSuitabilityFullyAligned = (suitability = {}) =>
  suitability.riskTolerance === true &&
  suitability.objective === true &&
  suitability.horizon === true;

export const validateSuitabilityMismatchRemark = (value, values) => {
  const suitability = values?.arForm?.suitability ?? {};
  if (isSuitabilityFullyAligned(suitability)) return null;
  if (!String(value ?? "").trim()) {
    return "Mismatch details is required when suitability is not fully aligned";
  }
  return null;
};

export const hasComplexProductInArForm = (arForm = {}) => {
  const recommended = (arForm.recommendedProducts ?? []).map(normalizeArFormProduct);
  const alternatives = normalizeAlternativesConsideredList(
    arForm.alternativesConsidered
  );
  return [...recommended, ...alternatives].some(
    (product) => product.complexProduct === true
  );
};

export const validateComplexProductConcentration = (value, values) => {
  const arForm = values?.arForm ?? {};
  if (!hasComplexProductInArForm(arForm)) return null;

  const num =
    value === "" || value == null || value === undefined
      ? null
      : Number(value);
  if (num == null || Number.isNaN(num) || num <= 0) {
    return "Complex product concentration % is required when a complex product is selected";
  }
  return null;
};

export const createValidateNonWrapUpfrontFee =
  (rules = []) =>
  (value, values) => {
    const arForm = values?.arForm ?? {};
    const commission = arForm.commission ?? {};
    if (commission.accountType !== "NonWrap") return null;

    const signDate = arForm.advisorDeclaration?.signDate;
    const settingsMin = resolveMinimumNonWrapUpfrontFee(rules, signDate);
    const threshold = resolveNonWrapEffectiveMinimum({
      submissionVia: arForm.submissionVia || "Online",
      settingsMin,
    });
    const num =
      value === "" || value == null || value === undefined
        ? null
        : Number(value);

    if (num == null || Number.isNaN(num) || num < threshold) {
      return `Upfront fee / switch fee % must be at least ${threshold}% (Online: 0.3%, Paper: 0.5%)`;
    }
    return null;
  };

export const ADVISOR_ONLINE_SIGNATURE = "Online submission to iFast";

export const getPrimaryConsultantFromForm = (form) => {
  const consultants = form?.values?.orderId?.consultants;
  if (!Array.isArray(consultants) || !consultants.length) return null;
  return consultants[0];
};

export const buildAdvisorDeclarationDefaults = (consultant) => ({
  signature: ADVISOR_ONLINE_SIGNATURE,
  signDate: new Date(),
  staffCode: consultant?.staffId ?? "",
  sfcCeNo: consultant?.consultantInfo?.sfcNumber ?? "",
});

export const formatArFormDisplayDate = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return moment(date).format("DD/MM/YYYY");
};

export const resolveAdvisorDeclaration = (advisorDeclaration, consultant) => {
  const defaults = buildAdvisorDeclarationDefaults(consultant);
  return {
    signature: advisorDeclaration?.signature || defaults.signature,
    signDate: advisorDeclaration?.signDate || defaults.signDate,
    staffCode: defaults.staffCode || advisorDeclaration?.staffCode || "",
    sfcCeNo: defaults.sfcCeNo || advisorDeclaration?.sfcCeNo || "",
    remark: advisorDeclaration?.remark ?? "",
  };
};

export const getArFormValues = (form) => {
  const current = form?.values?.arForm;
  if (!current || Object.keys(current).length === 0) return DEFAULT_AR_FORM();
  return {
    ...DEFAULT_AR_FORM(),
    ...current,
    submissionVia: current.submissionVia || "Online",
    suitability: {
      ...DEFAULT_AR_FORM().suitability,
      ...(current.suitability ?? {}),
    },
    commission: {
      ...DEFAULT_AR_FORM().commission,
      ...(current.commission ?? {}),
    },
    clientDeclaration: {
      ...DEFAULT_AR_FORM().clientDeclaration,
      ...(current.clientDeclaration ?? {}),
    },
    advisorDeclaration: {
      ...DEFAULT_AR_FORM().advisorDeclaration,
      ...(current.advisorDeclaration ?? {}),
    },
    alternativesConsidered: normalizeAlternativesConsideredList(
      current.alternativesConsidered
    ),
    transactionType: normalizeTransactionTypes(current.transactionType),
  };
};

const AR_FORM_NESTED_GROUPS = new Set([
  "suitability",
  "commission",
  "clientDeclaration",
  "advisorDeclaration",
]);

const COMMISSION_NUMBER_FIELDS = new Set([
  "upfrontWrapFee",
  "switchFee",
  "ongoingWrapFee",
  "platformFee",
  "upfrontFee",
]);

const mergeArFormDefaults = (current = {}) => {
  const base = DEFAULT_AR_FORM();
  return {
    ...base,
    ...current,
    suitability: { ...base.suitability, ...(current.suitability ?? {}) },
    commission: { ...base.commission, ...(current.commission ?? {}) },
    clientDeclaration: {
      ...base.clientDeclaration,
      ...(current.clientDeclaration ?? {}),
    },
    advisorDeclaration: {
      ...base.advisorDeclaration,
      ...(current.advisorDeclaration ?? {}),
    },
    alternativesConsidered:
      current.alternativesConsidered ?? base.alternativesConsidered,
    recommendedProducts: current.recommendedProducts ?? base.recommendedProducts,
  };
};

const coerceNestedArFormValue = (group, field, value) => {
  if (group === "commission" && COMMISSION_NUMBER_FIELDS.has(field)) {
    return coerceArFormNumber(value);
  }
  return value;
};

const ensureArFormInitialized = (form) => {
  const current = form?.values?.arForm;
  if (!current || Object.keys(current).length === 0) {
    form.setFieldValue("arForm", DEFAULT_AR_FORM());
    return;
  }
  const base = DEFAULT_AR_FORM();
  if (!current.suitability) {
    form.setFieldValue("arForm.suitability", {
      ...base.suitability,
      ...(current.suitability ?? {}),
    });
  }
  if (!current.commission) {
    form.setFieldValue("arForm.commission", {
      ...base.commission,
      ...(current.commission ?? {}),
    });
  }
  if (!current.clientDeclaration) {
    form.setFieldValue("arForm.clientDeclaration", {
      ...base.clientDeclaration,
      ...(current.clientDeclaration ?? {}),
    });
  }
  if (!current.advisorDeclaration) {
    form.setFieldValue("arForm.advisorDeclaration", {
      ...base.advisorDeclaration,
      ...(current.advisorDeclaration ?? {}),
    });
  }
  if (!current.submissionVia) {
    form.setFieldValue("arForm.submissionVia", "Online");
  }
};

export const syncAdvisorDeclaration = (form, consultant) => {
  ensureArFormInitialized(form);
  const current = form.values?.arForm?.advisorDeclaration ?? {};
  const next = resolveAdvisorDeclaration(current, consultant);
  const unchanged =
    current.signature === next.signature &&
    current.staffCode === next.staffCode &&
    current.sfcCeNo === next.sfcCeNo &&
    formatArFormDisplayDate(current.signDate) ===
      formatArFormDisplayDate(next.signDate);
  if (!unchanged) {
    form.setFieldValue("arForm.advisorDeclaration", next);
  }
  return next;
};

export const updateArForm = (form, updater) => {
  form.markUserEdited?.();
  ensureArFormInitialized(form);
  const current = getArFormValues(form);
  const next =
    typeof updater === "function" ? updater(current) : { ...current, ...updater };
  form.setFieldValue("arForm", {
    ...(form.values.arForm ?? {}),
    ...next,
    suitability: next.suitability,
    commission: next.commission,
    clientDeclaration: next.clientDeclaration,
    advisorDeclaration: next.advisorDeclaration,
    alternativesConsidered: next.alternativesConsidered,
    recommendedProducts: next.recommendedProducts,
  });
  return next;
};

export const setArFormField = (form, field, value) => {
  form.markUserEdited?.();
  ensureArFormInitialized(form);
  const nextValue =
    field === "declaredNetWealth" ||
    field === "concentrationRiskLevel" ||
    field === "complexProductConcentration" ||
    field === "investmentAmount" ||
    field === "latestRpqLevel"
      ? coerceArFormNumber(value)
      : value;
  form.setFieldValue(`arForm.${field}`, nextValue);
};

export const setArFormNestedField = (form, path, value) => {
  form.markUserEdited?.();
  ensureArFormInitialized(form);

  const [group, field] = path.split(".");
  if (!group || !field || !AR_FORM_NESTED_GROUPS.has(group)) {
    form.setFieldValue(`arForm.${path}`, value);
    return;
  }

  if (group === "commission") {
    updateCommission(form, { [field]: value });
    return;
  }

  const nextValue = coerceNestedArFormValue(group, field, value);
  const defaultGroup = DEFAULT_AR_FORM()[group] ?? {};
  const currentGroup = form.values.arForm?.[group] ?? {};
  form.setFieldValue(`arForm.${group}`, {
    ...defaultGroup,
    ...currentGroup,
    [field]: nextValue,
  });
};

export const updateCommission = (form, patch) => {
  form.markUserEdited?.();
  ensureArFormInitialized(form);
  const commission = {
    ...DEFAULT_AR_FORM().commission,
    ...(form.values.arForm?.commission ?? {}),
  };

  Object.entries(patch).forEach(([field, value]) => {
    if (COMMISSION_NUMBER_FIELDS.has(field)) {
      const num = coerceArFormNumber(value);
      if (num === undefined) delete commission[field];
      else commission[field] = num;
      return;
    }
    if (value === undefined || value === null || value === "") {
      delete commission[field];
      return;
    }
    commission[field] = value;
  });

  form.setFieldValue("arForm.commission", commission);
};

export const setCommissionAccountType = (form, accountType) => {
  const patch = { accountType: accountType || undefined };
  if (accountType === "Wrap") {
    patch.upfrontFee = undefined;
    patch.switchFee = undefined;
  } else if (accountType === "NonWrap") {
    patch.upfrontWrapFee = undefined;
    patch.ongoingWrapFee = undefined;
    patch.platformFee = undefined;
    patch.switchFee = undefined;
  }
  updateCommission(form, patch);
};

export const calcConcentrationRiskLevel = (investmentAmount, declaredNetWealth) => {
  if (!investmentAmount || !declaredNetWealth) return null;
  return (investmentAmount / declaredNetWealth) * 100;
};

export const isRpqWithinLastYear = (rpqArray) => {
  const last = rpqArray?.[0];
  if (!last?.signDate) return false;
  return moment().diff(moment(last.signDate), "years", true) < 1;
};

export const getClientRpqSummary = (source = {}) => {
  const rpq = source?.rpq?.length
    ? source.rpq
    : source?.client?.rpq ?? [];
  const lastRpq = rpq[0];
  const status =
    rpq.length > 0
      ? resolveRpqStatus(rpq)
      : source?.rpqStatus ?? source?.client?.rpqStatus ?? "NONE";
  const withinLastYear = isRpqWithinLastYear(rpq);
  const riskLevel = lastRpq?.riskLevel;
  return {
    lastRpq,
    status,
    withinLastYear,
    riskLevel,
    riskLabel: riskLevel ? RPQ_RISK_LABELS[riskLevel] : null,
  };
};

const joinDisplayNames = (...parts) =>
  parts
    .map((part) => (part == null ? "" : String(part).trim()))
    .filter(Boolean)
    .join(" / ");

export const getClientDisplayName = (formValues) => {
  const client = formValues?.client;
  if (client?.name || client?.cname) {
    return joinDisplayNames(client.name, client.cname);
  }
  const applicant = formValues?.applicant;
  if (applicant?.name || applicant?.cname) {
    return joinDisplayNames(applicant.name, applicant.cname);
  }
  return "";
};

export const validateRecommendedDerivativesFunds = (value, values = {}) =>
  getDerivativesFundPurchaseError(
    resolveDerivativesEligibilityClient(values),
    value
  );

export const validateRecommendedRpqFunds = (value, values = {}) =>
  getRpqFundPurchaseError(resolveClientRpqFundContext(values), value);

export const validateRecommendedProductsEligibility = (value, values = {}) =>
  validateRecommendedDerivativesFunds(value, values);

export const createArFormValidators = (nonWrapUpfrontFeeRules = []) => ({
  "arForm.suitability.mismatchRemark": validateSuitabilityMismatchRemark,
  "arForm.complexProductConcentration": validateComplexProductConcentration,
  "arForm.commission.upfrontFee":
    createValidateNonWrapUpfrontFee(nonWrapUpfrontFeeRules),
  "arForm.recommendedProducts": validateRecommendedProductsEligibility,
});

export const collectArFormValidationErrors = (values, validators = {}) =>
  Object.entries(validators)
    .map(([path, validator]) => {
      const value = _.get(values, path);
      return validator(value, values);
    })
    .filter(Boolean);

/** Contract No. is ready when it contains letters or digits, including Void. */
export const isContractNoReady = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return false;
  return /[A-Za-z0-9]/.test(text);
};

export const isArFormOpsRole = (roleName) => {
  const name = String(roleName ?? "").toUpperCase().trim();
  if (!name) return false;
  if (
    name.includes("CONSULTANT") ||
    name.includes("ADVISOR") ||
    name.includes("CHANNEL") ||
    name.includes("AGENT")
  ) {
    return false;
  }
  return (
    name.includes("OPERATION") ||
    name.includes("ADMIN") ||
    name.includes("ACCOUNT")
  );
};

export const getContractNoPrintDisabledReason = (values) => {
  const funds = (values?.arForm?.recommendedProducts ?? [])
    .map(normalizeArFormProduct)
    .filter((product) => String(product.fundName ?? "").trim());

  if (!funds.length) {
    return "Enter a Contract No. (letters, numbers, or Void) for each fund before printing.";
  }

  const missing = funds.filter((product) => !isContractNoReady(product.contractNo));
  if (!missing.length) return null;

  const names = missing.map((product) => product.fundName.trim()).join(", ");
  return `Contract No. must contain letters, numbers, or Void for each fund: ${names}`;
};

export const getArFormPrintDisabledReason = ({
  transactionId,
  values,
  validators,
}) => {
  if (!transactionId) {
    return "Please save the transaction before printing.";
  }
  const errors = collectArFormValidationErrors(values, validators);
  const contractReason = getContractNoPrintDisabledReason(values);
  if (contractReason) errors.push(contractReason);
  if (!errors.length) return null;
  return errors.join("\n");
};

export const buildArFormValidationValues = (transaction, orderValues = {}) => ({
  arForm: {
    ...(transaction?.arForm ?? {}),
    ifastAccountNo: resolveOrderIfastAccountNo({
      policyNumber: orderValues?.policyNumber,
      ifastAccountNo: orderValues?.arForm?.ifastAccountNo ?? "",
    }),
  },
});
